import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TouchableOpacity,
  Dimensions,
  ActivityIndicator,
} from 'react-native';
import { useSelector } from 'react-redux';
import Svg, { Path } from 'react-native-svg';
import { useTheme } from '../../../../theme/useTheme';
import DLODropdown from '../DLODropdown';
import DLOButton from '../DLOButton';
import DLOModal from '../DLOModal';
import DLOTooltip from '../DLOTooltip';
import { createApiClient } from '../../../../api/apiClient';
import { DLODropdownOption } from '../../types/dlo.types';
import type { RootState } from '../../../../store';

// Icons from Figma
const SaveIcon = ({ color = '#3A3A3A' }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path 
      d="M14.1667 2.5H4.16667C3.24167 2.5 2.5 3.25 2.5 4.16667V15.8333C2.5 16.75 3.24167 17.5 4.16667 17.5H15.8333C16.75 17.5 17.5 16.75 17.5 15.8333V5.83333L14.1667 2.5ZM15.8333 15.8333H4.16667V4.16667H13.475L15.8333 6.525V15.8333ZM10 10C8.61667 10 7.5 11.1167 7.5 12.5C7.5 13.8833 8.61667 15 10 15C11.3833 15 12.5 13.8833 12.5 12.5C12.5 11.1167 11.3833 10 10 10ZM5 5H12.5V8.33333H5V5Z" 
      fill={color} 
    />
  </Svg>
);

const ClearIcon = ({ color = '#3A3A3A' }) => (
  <Svg width="17" height="15" viewBox="0 0 17 15" fill="none">
    <Path d="M12.6773 12.5H16.6356V14.1667H11.0106L12.6773 12.5ZM2.26064 14.1667L0.489805 12.3958C0.170361 12.0764 0.00716608 11.6806 0.000221631 11.2083C-0.00672281 10.7361 0.149527 10.3333 0.468972 10L9.63564 0.5C9.95508 0.166667 10.3474 0 10.8127 0C11.278 0 11.6704 0.159722 11.9898 0.479167L16.1356 4.625C16.4551 4.94444 16.6148 5.34028 16.6148 5.8125C16.6148 6.28472 16.4551 6.68056 16.1356 7L9.13564 14.1667H2.26064ZM8.42731 12.5L14.969 5.79167L10.844 1.66667L1.63564 11.1667L2.96897 12.5H8.42731Z" fill={color} />
  </Svg>
);

type DeleteIconProps = { color?: string; size?: number };
const DeleteIcon: React.FC<DeleteIconProps> = ({ color = '#D32F2F', size = 20 }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12z" fill={color} />
    <Path d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill={color} />
  </Svg>
);

// Radio Button Component
interface RadioButtonProps {
  value: string;
  label: string;
  selected: boolean;
  onSelect: (value: string) => void;
  ringColor?: string;
  dotColor?: string;
  labelColor?: string;
}

const RadioButton: React.FC<RadioButtonProps> = ({ value, label, selected, onSelect, ringColor = '#3A3A3A', dotColor = '#005CBC', labelColor = '#616161' }) => (
  <TouchableOpacity
    style={styles.radioButton}
    onPress={() => onSelect(value)}
    activeOpacity={0.7}
  >
    <Svg width="12" height="20" viewBox="0 0 12 20" fill="none">
      <Path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 10C12 13.3135 9.31372 16 6 16C2.68628 16 0 13.3135 0 10C0 6.68652 2.68628 4 6 4C9.31372 4 12 6.68652 12 10ZM11 10C11 12.7612 8.76147 15 6 15C3.23853 15 1 12.7612 1 10C1 7.23877 3.23853 5 6 5C8.76147 5 11 7.23877 11 10Z"
        fill={ringColor}
      />
      {selected && (
        <Path
          d="M8.5 10C8.5 11.3807 7.3807 12.5 6 12.5C4.6193 12.5 3.5 11.3807 3.5 10C3.5 8.6193 4.6193 7.5 6 7.5C7.3807 7.5 8.5 8.6193 8.5 10Z"
          fill={dotColor}
        />
      )}
    </Svg>
    <Text style={[styles.radioLabel, { color: labelColor }]}>{label}</Text>
  </TouchableOpacity>
);

// Table Entry Interface
interface TableEntry {
  id: string;
  time: string;
  timeFormat: string;
  shift: string;
}

export interface DLOTableProps {
  onSave?: () => void;
  onClear?: () => void;
  style?: any;
  data?: any;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const DLOTable: React.FC<DLOTableProps> = ({ onSave, onClear, style, data, loading = false, error = null, onRetry }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const [selectedTime, setSelectedTime] = useState<DLODropdownOption | null>(null);
  const [timeFormat, setTimeFormat] = useState<string>('');
  const [shift, setShift] = useState<string>('');
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const [savedEntries, setSavedEntries] = useState<TableEntry[]>([]);
  
  // Save state management
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalMessage, setModalMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  // Custom time input state
  const [customTime, setCustomTime] = useState<string>('');
  // Delete a saved entry and update API
  const handleDelete = async (entryId: string) => {
    // Validate location is selected
    if (!selectedLocation?.id) {
      setModalTitle('Error');
      setModalMessage('Missing location information. Please select a location.');
      setIsSuccess(false);
      setIsModalVisible(true);
      return;
    }

    setDeletingId(entryId);
    try {
      const api = createApiClient();
      // Exclude the entry to be deleted
      const updatedEntries = savedEntries.filter(entry => entry.id !== entryId);
      // Prepare shiftUpdates for API
      const formatTimeForApi = (time: string, format: string): string => {
        return `${time}:00`;
      };
      const shiftUpdates = updatedEntries.map(entry => {
        let timeValue = entry.time;
        let format = entry.timeFormat;
        if (timeValue.includes(' ')) {
          const parts = timeValue.split(' ');
          timeValue = parts[0];
          format = parts[1] || format;
        }
        return {
          time_value: formatTimeForApi(timeValue, format),
          time_format: format,
          shift_type: entry.shift
        };
      });
      const requestBody = {
        loc_id: selectedLocation.id,
        shiftUpdates
      };
      await api.put('/dlo/ShiftTimes', requestBody);
      setSavedEntries(updatedEntries);
      setModalTitle('Success');
      setModalMessage('Shift time deleted successfully');
      setIsSuccess(true);
      setIsModalVisible(true);
    } catch (error: any) {
      setModalTitle('Error');
      const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred while deleting shift time';
      setModalMessage(errorMessage);
      setIsSuccess(false);
      setIsModalVisible(true);
    } finally {
      setDeletingId(null);
    }
  };
  
  // Redux selectors
  const selectedLocation = useSelector((state: RootState) => state.appData.selectedLocation);

  useEffect(() => {
    const onChange = (result: any) => {
      setScreenData(result.window);
    };
    const subscription = Dimensions.addEventListener('change', onChange);
    return () => subscription?.remove();
  }, []);

  // Transform API data to TableEntry format
  useEffect(() => {
    if (data?.data && Array.isArray(data.data)) {
      const transformedEntries: TableEntry[] = data.data.map((item: any) => {
        // Convert 24-hour time back to 12-hour format for display
        const timeValue = item.time_value || '';
        let displayTime = timeValue.substring(0, 5); // Extract time part without seconds (e.g., "08:00")
        let timeFormat = item.time_format || 'AM';
        
        // If time_format is not provided, determine it from the time_value
        if (timeValue && !item.time_format) {
          const [hours] = timeValue.split(':');
          const hour24 = parseInt(hours);
          
          if (hour24 === 0) {
            displayTime = '12:' + timeValue.split(':')[1];
            timeFormat = 'AM';
          } else if (hour24 > 0 && hour24 < 12) {
            displayTime = timeValue.substring(0, 5);
            timeFormat = 'AM';
          } else if (hour24 === 12) {
            displayTime = '12:' + timeValue.split(':')[1];
            timeFormat = 'PM';
          } else if (hour24 > 12 && hour24 <= 23) {
            const hour12 = hour24 - 12;
            displayTime = hour12.toString().padStart(2, '0') + ':' + timeValue.split(':')[1];
            timeFormat = 'PM';
          }
        }
        
        return {
          id: item.id != null ? item.id.toString() : '',
          time: displayTime,
          timeFormat: timeFormat,
          shift: item.shift_type,
        };
      });
      setSavedEntries(transformedEntries);
    } else {
      setSavedEntries([]);
    }
  }, [data]);

  // Function to determine responsive width based on screen dimensions
  const getResponsiveWidth = () => {
    const { width, height } = screenData;
    const isLandscape = width > height;
    
    // Desktop (wide screens)
    if (width >= 1024) {
      return '50%';
    }
    // iPad landscape
    else if (width >= 768 && isLandscape) {
      return '70%';
    }
    // iPad portrait and mobile
    else {
      return '100%';
    }
  };

  // Validate custom time format (HH:MM)
  const validateCustomTime = (time: string): boolean => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  };

  // Base time options (without custom time)
  const baseTimeOptions: DLODropdownOption[] = [
    { id: '2', label: '01:00', value: '01:00' },
    { id: '3', label: '01:30', value: '01:30' },
    { id: '4', label: '02:00', value: '02:00' },
    { id: '5', label: '02:30', value: '02:30' },
    { id: '6', label: '03:00', value: '03:00' },
    { id: '7', label: '03:30', value: '03:30' },
    { id: '8', label: '04:00', value: '04:00' },
    { id: '9', label: '04:30', value: '04:30' },
    { id: '10', label: '05:00', value: '05:00' },
    { id: '11', label: '05:30', value: '05:30' },
    { id: '12', label: '06:00', value: '06:00' },
    { id: '13', label: '06:30', value: '06:30' },
    { id: '14', label: '07:00', value: '07:00' },
    { id: '15', label: '07:30', value: '07:30' },
    { id: '16', label: '08:00', value: '08:00' },
    { id: '17', label: '08:30', value: '08:30' },
    { id: '18', label: '09:00', value: '09:00' },
    { id: '19', label: '09:30', value: '09:30' },
    { id: '20', label: '10:00', value: '10:00' },
    { id: '21', label: '10:30', value: '10:30' },
    { id: '22', label: '11:00', value: '11:00' },
    { id: '23', label: '11:30', value: '11:30' },
    { id: '24', label: '12:00', value: '12:00' },
    { id: '25', label: '12:30', value: '12:30' },
  ];

  // Dynamic timeOptions that includes custom time as ID 1 if available
  const timeOptions: DLODropdownOption[] = (() => {
    const options = [...baseTimeOptions];
    
    // If there's a valid custom time and it's not already in the base options
    if (validateCustomTime(customTime) && !baseTimeOptions.some(opt => opt.value === customTime)) {
      options.unshift({
        id: '1',
        label: customTime,
        value: customTime,
      });
    }
    
    return options;
  })();

  // Handle custom time input change
  const handleCustomTimeChange = (text: string) => {
    setCustomTime(text);
    
    // If valid time is entered, automatically select it
    if (validateCustomTime(text)) {
      const customOption: DLODropdownOption = {
        id: '1',
        label: text,
        value: text,
      };
      setSelectedTime(customOption);
    }
  };

  // Handle custom time submit (when user presses Enter)
  const handleCustomTimeSubmit = () => {
    if (validateCustomTime(customTime)) {
      const customOption: DLODropdownOption = {
        id: '1',
        label: customTime,
        value: customTime,
      };
      setSelectedTime(customOption);
    }
  };

  // Handle dropdown selection properly
  const handleTimeSelection = (option: DLODropdownOption | DLODropdownOption[] | null) => {
    if (Array.isArray(option)) {
      const selectedOption = option[0] || null;
      setSelectedTime(selectedOption);
    } else {
      setSelectedTime(option);
    }
  };

  const handleSave = async () => {
    // Validate that all fields are filled
    if (!selectedTime || !timeFormat || !shift) {
      setModalTitle('Validation Error');
      setModalMessage('Please fill all fields before saving');
      setIsSuccess(false);
      setIsModalVisible(true);
      return;
    }



    // Validate location is selected
    if (!selectedLocation?.id) {
      setModalTitle('Error');
      setModalMessage('Missing location information. Please select a location.');
      setIsSuccess(false);
      setIsModalVisible(true);
      return;
    }

    setIsSaving(true);

    try {
      const api = createApiClient();
      
      // Use the selected time value directly
      const actualTime = selectedTime.value;
      
      // Create new entry for local state
      const newEntry: TableEntry = {
        id: Date.now().toString(), // Simple ID generation
        time: actualTime,
        timeFormat,
        shift,
      };

      // Format time for API (keep in 12-hour format, just add seconds)
      const formatTimeForApi = (time: string, format: string): string => {
        // Simply add seconds to the 12-hour format time
        return `${time}:00`;
      };

      // Prepare new shift update
      const newShiftUpdate = {
        time_value: formatTimeForApi(actualTime, timeFormat),
        time_format: timeFormat,
        shift_type: shift,
      };

      // Get existing saved entries and add new one for API call
      const allShiftUpdates = [
        ...savedEntries.map(entry => {
          // For existing entries, try to parse the time correctly
          let timeValue = entry.time;
          let format = entry.timeFormat;
          
          // If the time already includes format (e.g., "08:00 AM"), split it
          if (timeValue.includes(' ')) {
            const parts = timeValue.split(' ');
            timeValue = parts[0];
            format = parts[1] || format;
          }
          
          return {
            time_value: formatTimeForApi(timeValue, format),
            time_format: format,
            shift_type: entry.shift
          };
        }),
        newShiftUpdate
      ];

      const requestBody = {
        loc_id: selectedLocation.id,
        shiftUpdates: allShiftUpdates
      };

      const response = await api.put('/dlo/ShiftTimes', requestBody);

      // Success
      setModalTitle('Success');
      setModalMessage('Shift times updated successfully');
      setIsSuccess(true);
      setIsModalVisible(true);

      // Add to saved entries in local state
      setSavedEntries(prev => [...prev, newEntry]);

      // Clear the input fields
      setSelectedTime(null);
      setTimeFormat('');
      setShift('');
      setCustomTime('');

      // Call parent onSave if provided
      onSave?.();

    } catch (error: any) {
      // Error handling
      setModalTitle('Error');
      const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred while saving shift times';
      setModalMessage(errorMessage);
      setIsSuccess(false);
      setIsModalVisible(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    if (isSuccess) {
      // Additional success actions can be added here
    }
  };

  const handleClear = () => {
    setSelectedTime(null);
    setTimeFormat('');
    setShift('');
    setCustomTime('');
    console.log('Clear clicked');
    onClear?.();
  };

  return (
    <View style={[styles.container, { maxWidth: getResponsiveWidth(), backgroundColor: isDark ? colors.background.elevated : '#FBFCFE', borderColor: isDark ? colors.border.default : '#E0E0E0' }, style]}>
      {/* Loading State */}
      {loading && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.brand.primary} />
          <Text style={[styles.loadingText, { color: colors.text.primary }]}>Loading shift times...</Text>
        </View>
      )}

      {/* Error State */}
      {error && !loading && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Error loading shift times</Text>
          <Text style={[styles.errorMessage, { color: colors.text.primary }]}>{error}</Text>
          {onRetry && (
            <DLOButton
              text="Retry"
              variant="primary"
              size="medium"
              onPress={onRetry}
            />
          )}
        </View>
      )}

      {/* Table Content - Only show when not loading and no error */}
      {!loading && !error && (
        <>
          {/* Main Table Header */}
          <View style={[styles.mainTableHeader, { backgroundColor: colors.brand.primary }]}>
            <View style={styles.mainHeaderRow}>
              <View style={styles.startTimesMainSection}>
                <Text style={styles.mainHeaderTitle}>START TIMES</Text>
              </View>
              <View style={styles.mainHeaderDivider} />
              <View style={styles.actionMainSection}>
                <Text style={styles.mainHeaderTitle}>ACTION</Text>
              </View>
            </View>
          </View>

      {/* Sub Headers */}
      <View style={[styles.subHeaders, { backgroundColor: isDark ? colors.background.surface : '#EFF5FC', borderBottomColor: isDark ? colors.border.default : '#E0E0E0' }]}>
        {/* START TIMES Sub-header Group */}
        <View style={styles.startTimesSubGroup}>
          <View style={styles.timeSubColumn}>
            <Text style={[styles.subHeaderText, { fontWeight: 'bold', color: colors.text.primary }]}>Time</Text>
          </View>

          <View style={[styles.subColumnDivider, { backgroundColor: isDark ? colors.border.default : '#E0E0E0' }]} />

          <View style={styles.timeFormatSubColumn}>
            <Text style={[styles.subHeaderText, { fontWeight: 'bold', color: colors.text.primary }]}>Time Format</Text>
          </View>

          <View style={[styles.subColumnDivider, { backgroundColor: isDark ? colors.border.default : '#E0E0E0' }]} />

          <View style={styles.shiftSubColumn}>
            <Text style={[styles.subHeaderText, { fontWeight: 'bold', color: colors.text.primary }]}>Shift</Text>
          </View>
        </View>
        
        {/* Main Divider between groups */}
        <View style={[styles.subHeaderMainDivider, { backgroundColor: isDark ? colors.border.default : '#E0E0E0' }]} />
        
        {/* ACTION Sub-header Group */}
        <View style={styles.actionSubGroup}>
          <View style={styles.actionSubColumn}>
            <Text style={[styles.subHeaderText, { color: colors.text.primary }]}> </Text>
          </View>
        </View>
      </View>

      {/* Data Row */}
      <View style={[styles.dataRow, { backgroundColor: isDark ? colors.background.elevated : '#FFFFFF', borderBottomColor: isDark ? colors.border.default : '#E0E0E0' }]}>
        {/* START TIMES Data Group */}
        <View style={styles.startTimesDataGroup}>
          {/* Time Dropdown */}
          <View style={styles.timeSubColumn}>
            <DLODropdown
              value={selectedTime}
              options={timeOptions}
              onSelect={handleTimeSelection}
              placeholder="Select Time"
              showLabel={false}
              size='medium'
              fullWidth={true}
              style={styles.timeDropdown}
              placement="bottom"
              offset={2}
              minDropdownWidth={120}
              placeholderStyle={{ textAlign: 'center', width: '100%' }}
              customTextInput={{
                show: true,
                placeholder: "Enter Custom Time (HH:MM)",
                value: customTime,
                onTextChange: handleCustomTimeChange,
                onSubmit: handleCustomTimeSubmit,
                validate: validateCustomTime,
                //errorMessage: "Please enter a valid time in HH:MM format (e.g., 08:45)",
              }}
            />
          </View>
          <View style={[styles.subColumnDataDivider, { backgroundColor: isDark ? colors.border.default : '#E0E0E0' }]} />

          {/* Time Format Radio Buttons */}
          <View style={styles.timeFormatSubColumn}>
            <View style={styles.radioContainer}>
              <RadioButton
                value="AM"
                label="AM"
                selected={timeFormat === 'AM'}
                onSelect={setTimeFormat}
                ringColor={isDark ? '#FFFFFF' : '#3A3A3A'}
                dotColor={colors.brand.primary}
                labelColor={isDark ? colors.text.primary : '#616161'}
              />
              <RadioButton
                value="PM"
                label="PM"
                selected={timeFormat === 'PM'}
                onSelect={setTimeFormat}
                ringColor={isDark ? '#FFFFFF' : '#3A3A3A'}
                dotColor={colors.brand.primary}
                labelColor={isDark ? colors.text.primary : '#616161'}
              />
            </View>
          </View>
          <View style={[styles.subColumnDataDivider, { backgroundColor: isDark ? colors.border.default : '#E0E0E0' }]} />

          {/* Shift Radio Buttons */}
          <View style={styles.shiftSubColumn}>
            <View style={styles.radioContainer}>
              <RadioButton
                value="5x8"
                label="5x8"
                selected={shift === '5x8'}
                onSelect={setShift}
                ringColor={isDark ? '#FFFFFF' : '#3A3A3A'}
                dotColor={colors.brand.primary}
                labelColor={isDark ? colors.text.primary : '#616161'}
              />
              <RadioButton
                value="5x4"
                label="5x4"
                selected={shift === '5x4'}
                onSelect={setShift}
                ringColor={isDark ? '#FFFFFF' : '#3A3A3A'}
                dotColor={colors.brand.primary}
                labelColor={isDark ? colors.text.primary : '#616161'}
              />
              <RadioButton
                value="4x10"
                label="4x10"
                selected={shift === '4x10'}
                onSelect={setShift}
                ringColor={isDark ? '#FFFFFF' : '#3A3A3A'}
                dotColor={colors.brand.primary}
                labelColor={isDark ? colors.text.primary : '#616161'}
              />
            </View>
          </View>
          {/* <View style={styles.subColumnDataDivider} /> */}
        </View>

        {/* Main Divider between groups */}
        <View style={[styles.dataMainDivider, { backgroundColor: isDark ? colors.border.default : '#E0E0E0' }]} />

        {/* ACTION Data Group */}
        <View style={styles.actionDataGroup}>
          <View style={styles.actionSubColumn}>
            <View style={styles.actionButtons}>
              <DLOTooltip 
                content="Save" 
                position="top" 
                trigger="hover" 
                verticalOffset={30}
                horizontalOffset={-10}
                maxWidth={45}
                minWidth={60}
                padding={6}
                textAlign="center"
              >
                <TouchableOpacity
                  style={[
                    styles.actionButton,
                    isSaving && styles.disabledButton,
                    (isSaving || !selectedTime || !timeFormat || !shift) && styles.disabledButton
                  ]}
                  onPress={handleSave}
                  onPressIn={() => setHoveredButton('save')}
                  onPressOut={() => setHoveredButton(null)}
                  activeOpacity={0.7}
                  disabled={isSaving || !selectedTime || !timeFormat || !shift}
                >
                  {isSaving ? (
                    <ActivityIndicator size="small" color={isDark ? '#FFFFFF' : '#3A3A3A'} />
                  ) : (
                    <SaveIcon color={hoveredButton === 'save' ? colors.brand.primary : (isDark ? '#FFFFFF' : '#3A3A3A')} />
                  )}
                </TouchableOpacity>
              </DLOTooltip>
              <DLOTooltip 
                content="Clear" 
                position="top" 
                trigger="hover" 
                verticalOffset={30}
                horizontalOffset={-10}
                maxWidth={45}
                minWidth={60}
                padding={6}
                textAlign="center"
              >
                <TouchableOpacity
                  style={styles.actionButton}
                  onPress={handleClear}
                  onPressIn={() => setHoveredButton('clear')}
                  onPressOut={() => setHoveredButton(null)}
                  activeOpacity={0.7}
                >
                  <ClearIcon color={hoveredButton === 'clear' ? colors.brand.primary : (isDark ? '#FFFFFF' : '#3A3A3A')} />
                </TouchableOpacity>
              </DLOTooltip>
            </View>
          </View>
        </View>
      </View>

      {/* Saved Entries Rows */}
      {savedEntries.map((entry) => (
        <View key={entry.id} style={[styles.savedRow, { backgroundColor: isDark ? colors.background.elevated : '#FFFFFF' }]}>
          {/* START TIMES Data Group */}
          <View style={styles.startTimesDataGroup}>
            {/* Time Display */}
            <View style={styles.timeSubColumn}>
              <Text style={[styles.savedDataText, { color: colors.text.primary }]}>{entry.time}</Text>
            </View>
            <View style={[styles.subColumnDataDivider, { backgroundColor: isDark ? colors.border.default : '#E0E0E0' }]} />

            {/* Time Format Display */}
            <View style={styles.timeFormatSubColumn}>
              <Text style={[styles.savedDataText, { color: colors.text.primary }]}>{entry.timeFormat}</Text>
            </View>
            <View style={[styles.subColumnDataDivider, { backgroundColor: isDark ? colors.border.default : '#E0E0E0' }]} />

            {/* Shift Display */}
            <View style={styles.shiftSubColumn}>
              <Text style={[styles.savedDataText, { color: colors.text.primary }]}>{entry.shift}</Text>
            </View>
          </View>

          {/* Main Divider between groups */}
          <View style={[styles.dataMainDivider, { backgroundColor: isDark ? colors.border.default : '#E0E0E0' }]} />

          {/* ACTION Data Group */}
          <View style={styles.actionDataGroup}>
            <View style={styles.actionSubColumn}>
              <TouchableOpacity
                style={[styles.actionButton, deletingId === entry.id && styles.disabledButton]}
                onPress={() => handleDelete(entry.id)}
                disabled={!!deletingId}
                activeOpacity={0.7}
              >
                {deletingId === entry.id ? (
                  <ActivityIndicator size="small" color={isDark ? '#FFFFFF' : '#3A3A3A'} />
                ) : (
                  <DeleteIcon color="#D32F2F" />
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      ))}
        </>
      )}

      {/* Modal for success/error messages */}
      <DLOModal
        visible={isModalVisible}
        onClose={handleModalClose}
        title={modalTitle}
        primaryButtonText="OK"
        secondaryButtonText=""
        onPrimaryPress={handleModalClose}
        hideButtons={false}
        showCloseIcon={false}
        width={400}
      >
        <Text style={[styles.modalMessage, { color: colors.text.primary }]}>
          {modalMessage}
        </Text>
      </DLOModal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    overflow: 'hidden',
    width: '100%',
  },
  // Main Table Header Section
  mainTableHeader: {
    backgroundColor: '#005CBC',
  },
  mainHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    //paddingVertical: 16,
  },
  startTimesMainSection: {
    flex: 5, // Spans across Time(2) + TimeFormat(1.5) + Shift(1.5) = 5
    alignItems: 'center',
    // paddingLeft: Platform.select({
    //   web: 16,
    //   default: 24,
    // }),
    paddingRight: 0,
  },
  mainHeaderTitle: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    paddingVertical: Platform.select({
      web: 12,
      default: 16,
    }),
  },
  mainHeaderDivider: {
    width: 1,
    height: '100%',
    backgroundColor: '#FFFFFF',
    marginHorizontal: Platform.select({
      web: 8,
      default: 16,
    }),
  },
  actionMainSection: {
    flex: 1,
    alignItems: 'center',
    paddingLeft: 0,
    // paddingRight: Platform.select({
    //   web: 16,
    //   default: 24,
    // }),
  },
  // Sub Headers Section
  subHeaders: {
    backgroundColor: '#EFF5FC',
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  startTimesSubGroup: {
    flex: 5, // Matches the main header proportion
    flexDirection: 'row',
    alignItems: 'center',
  },
  startTimesDataGroup: {
    flex: 5, // Matches the main header and sub header proportion
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
  },
  actionSubGroup: {
    flex: 1, // Matches the main header proportion
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingRight: 0,
  },
  actionDataGroup: {
    flex: 1, // Matches the main header and sub header proportion
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingRight: 0,
    height: '100%',
  },
  subHeaderMainDivider: {
    width: 1,
    height: 40,
    backgroundColor: '#E0E0E0',
    marginHorizontal: Platform.select({
      web: 8,
      default: 16,
    }),
    alignSelf: 'center',
  },
  dataMainDivider: {
    width: 1,
    backgroundColor: '#E0E0E0',
    marginHorizontal: Platform.select({
      web: 8,
      default: 16,
    }),
    alignSelf: 'stretch',
  },
  subHeaderText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '500',
    textAlign: 'center',
  },
  timeSubColumn: {
    flex: 2,
    alignItems: 'center',
    paddingLeft: Platform.select({
      web: 16,
      default: 24,
    }),
    paddingRight: Platform.select({
      web: 8,
      default: 12,
    }),
    paddingVertical: Platform.select({
      web: 8,
      default: 12,
    }),
    justifyContent: 'center',
    minWidth: 0, // Allow shrinking
  },
  timeFormatSubColumn: {
    flex: 1.5,
    alignItems: 'center',
    paddingLeft: Platform.select({
      web: 8,
      default: 12,
    }),
    paddingRight: Platform.select({
      web: 8,
      default: 12,
    }),
    paddingVertical: Platform.select({
      web: 8,
      default: 12,
    }),
    justifyContent: 'center',
    minWidth: 0, // Allow shrinking
  },
  shiftSubColumn: {
    flex: 1.5,
    alignItems: 'center',
    paddingHorizontal: Platform.select({
      web: 8,
      default: 12,
    }),
    paddingVertical: Platform.select({
      web: 8,
      default: 12,
    }),
    justifyContent: 'center',
    minWidth: 0, // Allow shrinking
  },
  actionSubColumn: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: Platform.select({
      web: 8,
      default: 12,
    }),
    paddingVertical: Platform.select({
      web: 8,
      default: 12,
    }),
    paddingRight: Platform.select({
      web: 16,
      default: 24,
    }),
    justifyContent: 'center',
    minWidth: 0, // Allow shrinking
  },
  subColumnDivider: {
    width: 1,
    height: 40,
    backgroundColor: '#E0E0E0',
    marginHorizontal: Platform.select({
      web: 4,
      default: 8,
    }),
    alignSelf: 'center',
  },
  subColumnDataDivider: {
    width: 1,
    backgroundColor: '#E0E0E0',
    marginHorizontal: Platform.select({
      web: 4,
      default: 8,
    }),
    alignSelf: 'stretch',
  },
  // Legacy styles (keeping for compatibility)
  tableHeader: {
    backgroundColor: '#005CBC',
    marginVertical: 16,
    paddingHorizontal: 24,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  startTimesSection: {
    flex: 5,
    alignItems: 'center',
  },
  startTimesTitle: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  headerDivider: {
    width: 1,
    height: 24,
    backgroundColor: '#FFFFFF',
    marginHorizontal: 16,
  },
  actionSection: {
    flex: 1,
    alignItems: 'flex-end',
    paddingRight: 8,
  },
  actionTitle: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  // Column Headers Section (Legacy)
  columnHeaders: {
    backgroundColor: '#EFF5FC',
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 12,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  columnHeaderText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '500',
    textAlign: 'center',
  },
  // Column Definitions
  timeColumn: {
    flex: 2,
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  timeFormatColumn: {
    flex: 1.5,
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  shiftColumn: {
    flex: 1.5,
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  actionColumn: {
    flex: 1,
    alignItems: 'flex-end',
    paddingHorizontal: 4,
    paddingRight: 8,
  },
  columnDivider: {
    width: 1,
    height: 32,
    backgroundColor: '#E0E0E0',
    marginHorizontal: 8,
  },
  // Data Row Section
  dataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: Platform.select({
      web: 56,
      default: 64,
    }),
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
  // Dropdown Styles
  timeDropdown: {
    width: '100%',
    maxWidth: '100%',
    textAlign: 'center',
  },
  // Radio Button Styles
  radioContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: Platform.select({
      web: 8,
      default: 12,
    }),
    flexWrap: 'wrap', // Allow wrapping on small screens
  },
  radioButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Platform.select({
      web: 4,
      default: 6,
    }),
  },
  radioLabel: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: Platform.select({
      web: 16,
      default: 20,
    }),
  },
  // Action Button Styles
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: Platform.select({
      web: 8,
      default: 12,
    }),
    flexWrap: 'wrap', // Allow wrapping on small screens
  },
  actionButton: {
    padding: Platform.select({
      web: 6,
      default: 8,
    }),
    borderRadius: 4,
    backgroundColor: 'transparent',
  },
  disabledButton: {
    opacity: 0.5,
  },
  // Saved Entries Styles
  savedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: Platform.select({
      web: 56,
      default: 64,
    }),
    backgroundColor: '#FFFFFF',
  },
  savedDataText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: Platform.select({
      web: 12,
      default: 14,
    }),
    fontWeight: '400',
    textAlign: 'center',
    lineHeight: Platform.select({
      web: 16,
      default: 20,
    }),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    minHeight: 200,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    minHeight: 200,
  },
  errorText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#E53E3E',
    marginBottom: 8,
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  errorMessage: {
    fontSize: 14,
    color: '#3A3A3A',
    marginBottom: 20,
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
  modalMessage: {
    fontSize: 14,
    color: '#3A3A3A',
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
  },
});

export default DLOTable;
